import type { IPathResult } from '../../astar/astarInterface'
import type { PImage } from '../../engine/aliases'
import type { IDeadish, IEntity } from '../../entities/entitiesUtils'
import { entitiesUtils } from '../../entities/entitiesUtils-sidecar'
import type { IIJ } from '../../lib/distanceUtil'
import { l } from '../../lib/imports'
import { createId } from '../../main/common'
import { common } from '../../main/common-sidecar'
import { sprites } from '../../sprites/sprites-sidecar'
import { spritesUtil } from '../../sprites/spritesUtil-sidecar'
import type { IVoyageCarryable } from './voyageCarryable'

import { voyageCarryable } from './voyageCarryable-sidecar'
import { voyageColors } from './voyageColors-sidecar'
import type { IVoyageCrewWeaponState } from './voyageCrewWeaponData'
import { voyageCrewWeaponData } from './voyageCrewWeaponData-sidecar'
import type { IVoyageCrewAlien } from './voyageCrew_alien'
import { voyageCrew_alien } from './voyageCrew_alien-sidecar'
import type { IVoyageCrewCrew } from './voyageCrew_crew'
import { voyageCrew_crew } from './voyageCrew_crew-sidecar'
import type { IVoyageCrewFire } from './voyageCrew_fire'
import { voyageCrew_fire } from './voyageCrew_fire-sidecar'
import { voyageCrew_fireable } from './voyageCrew_fireable-sidecar'
import type { IVoyageCrewHullBreach } from './voyageCrew_hullBreach'
import { voyageCrew_hullBreach } from './voyageCrew_hullBreach-sidecar'
import { voyageCrew_movable } from './voyageCrew_movable-sidecar'
import { voyageFakeInfo } from './voyageFakeInfo-sidecar'
import { voyageFloatingText } from './voyageFloatingText-sidecar'
import { voyageGameStatus } from './voyageGameStatus-sidecar'
import { voyageLayers } from './voyageLayers-sidecar'
import { voyageSounds } from './voyageSounds-sidecar'
import { voyageTilemap } from './voyageTilemap-sidecar'
import type { IVoyageTilemapPart } from './voyageTilemapPart'

export enum EVoyageCrewType {
  Default = -1,
  Null = 0,
  Crew = 1,
  Fire = 2,
  Alien = 3,
  HullBreach = 4,
}

export interface IVoyageCrew extends IEntity, IDeadish {
  type: EVoyageCrewType
  image: PImage
  x: number
  y: number
  i: number
  j: number
  name: string
  title: string
  info1: string
  team: number
  path: IPathResult<IVoyageTilemapPart> | null
  pathIdx: number
  moveTimer: number
  footstepTimer: number
  hitpoints: number
  fireState: IVoyageCrewFire | null
  breachState: IVoyageCrewHullBreach | null
  crewState: IVoyageCrewCrew | null
  alienState: IVoyageCrewAlien | null
  weaponState: IVoyageCrewWeaponState | null
  carryable: IVoyageCarryable | null
}

const objs: IVoyageCrew[] = []
export function getAll() {
  return objs
}

export function getAt(iij: IIJ, filter?: (f: IVoyageCrew) => boolean) {
  for (let i = 0; i < objs.length; i++) {
    let c = objs[i]
    if (iij.i === c.i && iij.j === c.j) {
      if (!filter || filter(c)) {
        return c
      }
    }
  }
  return null
}

export function create(type: EVoyageCrewType, team: number): IVoyageCrew {
  let frame = sprites.voyTileCrew[0]
  if (type === EVoyageCrewType.Fire) {
    frame = sprites.voyTileFire[0]
  }
  if (type === EVoyageCrewType.HullBreach) {
    frame = sprites.voyTileChecker2[0]
  }

  let image = spritesUtil.createImage(voyageLayers.getLayers().shipCrew, frame)

  let info1 = ''

  let id = createId()
  let name = '' + id
  if (type === EVoyageCrewType.Fire) {
    name = 'Fire ' + name
    image.tint = voyageColors.yellow
  }
  if (type === EVoyageCrewType.HullBreach) {
    name = 'Hull Breach ' + name
    image.tint = voyageColors.damage
  }
  if (type === EVoyageCrewType.Crew) {
    // name = 'Crew ' + name
    name = voyageFakeInfo.genFakeName()
    info1 = voyageFakeInfo.genFakeLike()
  }
  if (type === EVoyageCrewType.Alien) {
    name = 'Alien ' + name
    image.tint = voyageColors.green
  }
  let obj: IVoyageCrew = {
    id,
    idLoaded: -1,
    isDead: false,
    eType: 'voy-misc',
    image,
    team,
    type,
    x: 0,
    y: 0,
    i: -1,
    j: -1,
    name,
    info1,
    title: '',
    path: null,
    pathIdx: 0,
    moveTimer: 0,
    footstepTimer: 0,
    hitpoints: 100,
    fireState: null,
    crewState: null,
    breachState: null,
    alienState: null,
    weaponState: null,
    carryable: null,
  }
  objs.push(obj)

  if (type === EVoyageCrewType.Fire) {
    obj.fireState = voyageCrew_fire.create()
    obj.hitpoints = 1
  }

  if (type === EVoyageCrewType.HullBreach) {
    obj.breachState = voyageCrew_hullBreach.create()
    obj.hitpoints = 10
  }
  if (type === EVoyageCrewType.Crew) {
    obj.crewState = voyageCrew_crew.create()
    obj.weaponState = voyageCrewWeaponData.create(
      voyageCrewWeaponData.getCrewBlaster(),
    )
    obj.hitpoints = 100
    obj.title = voyageFakeInfo.genFakeTitle()

    // obj.carryable = voyageCarryable.create(EVoyageCarryableType.Coffee)
  }
  if (type === EVoyageCrewType.Alien) {
    obj.alienState = voyageCrew_alien.create()
    obj.weaponState = voyageCrewWeaponData.create(
      voyageCrewWeaponData.getAlienBlaster(),
    )
    obj.hitpoints = 100
    obj.title = 'Alien Scum'
  }
  return obj
}

export function moveToIJ(obj: IVoyageCrew, iij: IIJ) {
  obj.i = iij.i
  obj.j = iij.j
  let t = voyageTilemap.getSafe(iij.i, iij.j)
  if (t) {
    moveToXY(obj, t.x, t.y)

    if (obj.type === EVoyageCrewType.Fire) {
      let tCur = voyageTilemap.getSafeIJ(obj)
      if (tCur && tCur.fire === obj) {
        tCur.fire = null
      }
      t.fire = obj
    }

    if (obj.type === EVoyageCrewType.HullBreach) {
      let tCur = voyageTilemap.getSafeIJ(obj)
      if (tCur && tCur.breach === obj) {
        tCur.breach = null
      }
      t.breach = obj
    }

    if (obj.type === EVoyageCrewType.Crew) {
      if (t.system) {
        if (t.system.data.onEnter) {
          t.system.data.onEnter(obj)
        }
      }
    }
  }
}

export function moveToXY(obj: IVoyageCrew, x: number, y: number) {
  obj.x = x
  obj.y = y
  obj.image.x = x
  obj.image.y = y
  if (obj.carryable) {
    voyageCarryable.moveToXY(obj.carryable, x, y)
  }
}

export function kill(obj: IVoyageCrew) {
  obj.isDead = true

  if (obj.type === EVoyageCrewType.Fire) {
    let tCur = voyageTilemap.getSafeIJ(obj)
    if (tCur && tCur.fire === obj) {
      tCur.fire = null
    }
  }

  if (obj.type === EVoyageCrewType.HullBreach) {
    let tCur = voyageTilemap.getSafeIJ(obj)
    if (tCur && tCur.breach === obj) {
      tCur.breach = null
    }
  }
}
export function killAll() {
  l.forEach(objs, (c) => {
    kill(c)
  })
  removeDead()
}

export function updateAll() {
  l.forEach(objs, (c) => {
    update(c)
  })
  removeDead()
}
export function removeDead() {
  entitiesUtils.removeDead(objs, (c) => {
    c.image.destroy()
    if (c.carryable) {
      voyageCarryable.kill(c.carryable)
    }
  })
}
function update(obj: IVoyageCrew) {
  let ds = common.getUpdateDeltaScaled()

  if (obj.isDying || obj.isDead) {
    return
  }

  if (obj.type === EVoyageCrewType.Fire) {
    voyageCrew_fire.update(obj)
  }

  if (obj.type === EVoyageCrewType.HullBreach) {
    voyageCrew_hullBreach.update(obj)
  }

  if (obj.type === EVoyageCrewType.Crew) {
    voyageCrew_movable.update(obj)
    voyageCrew_fireable.update(obj)
    voyageCrew_crew.update(obj)
  }

  if (obj.type === EVoyageCrewType.Alien) {
    voyageCrew_movable.update(obj)
    voyageCrew_fireable.update(obj)
    voyageCrew_alien.update(obj)
    // console.log('update aliens')
  }
}

export function say(obj: IVoyageCrew, text: string) {
  if (!text) {
    return
  }
  voyageFloatingText.create(obj.image.x, obj.image.y - 16, text)
}

export function hurt(obj: IVoyageCrew, damage: number, reason: string) {
  obj.hitpoints -= damage
  if (obj.hitpoints <= 0) {
    makeDeadish(obj, reason)
  }
}

export function makeDeadish(obj: IVoyageCrew, reason: string) {
  if (!obj.isDying) {
    if (obj.type === EVoyageCrewType.Alien) {
      voyageSounds.playDeathAlien()
      voyageGameStatus.addAlienDead()
    }
    if (obj.type === EVoyageCrewType.Crew) {
      voyageSounds.playDeathCrewmember()
      voyageGameStatus.addDeadLog(obj.name + ' (' + obj.title + ') ' + reason)
    }
    obj.image.tint = voyageColors.g2
    obj.isDying = true
  }
}
